import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import styled from "styled-components";

import {
  buildCourseRouteTypeInlineLabels,
  CourseIndicatorIcon,
  LocalizationProvider,
} from "@ryde/controls";
import { useRtl } from "@ryde/redux";
import {
  convertUTCToISO,
  ICourseDto,
  IStyledAttributes,
  useFormats,
  useTimezone,
} from "@ryde/shared";
import { blueGreyMui } from "@ryde/theme";

export interface ICourseProduceDateRange {
  dateFrom: TDateISO;
  dateTo: TDateISO;
}

interface ICourseProduceDateRangeDialogContentProps {
  course: ICourseDto;
  initialFrom: TDateISO;
  initialTo: TDateISO;
  onChange: (range: ICourseProduceDateRange) => void;
}

export const CourseProduceDateRangeDialogContent: FC<
  ICourseProduceDateRangeDialogContentProps
> = ({ course, initialFrom, initialTo, onChange }) => {
  const { t } = useTranslation();
  const { dateFormat } = useFormats();
  const { direction } = useRtl();
  const { timezone } = useTimezone();
  const [dateFrom, setDateFrom] = useState<TDateISO>(initialFrom);
  const [dateTo, setDateTo] = useState<TDateISO>(initialTo);

  const courseStationsCount = course.metadata?.stationsInfo?.length ?? 0;

  const { routeTypeLabel, detailText, summaryLabel } = useMemo(
    () =>
      buildCourseRouteTypeInlineLabels({
        t,
        courseId: course.id,
        courseName: course.name,
        routeType: course.routeType,
        courseTotalDuration: course.metadata?.totalDuration,
        courseStationsCount,
      }),
    [
      course.id,
      course.name,
      course.routeType,
      course.metadata?.totalDuration,
      courseStationsCount,
      t,
    ],
  );

  useEffect(() => {
    onChange({ dateFrom, dateTo });
  }, [dateFrom, dateTo, onChange]);

  const handleFromChange = useEvent((value: dayjs.Dayjs | null): void => {
    if (!value?.isValid()) {
      return;
    }
    setDateFrom(convertUTCToISO(value));
  });

  const handleToChange = useEvent((value: dayjs.Dayjs | null): void => {
    if (!value?.isValid()) {
      return;
    }
    setDateTo(convertUTCToISO(value));
  });

  return (
    <CourseProduceDateRangeDialogContentStyled>
      <div className="course-produce-date-range__course-block">
        <div className="course-produce-date-range__course">
          <span className="course-produce-date-range__course-icon">
            <CourseIndicatorIcon routeType={course.routeType} />
          </span>
          <span className="course-produce-date-range__course-id">
            #{course.id}
          </span>
          {course.name ? (
            <span className="course-produce-date-range__course-name">
              {course.name}
            </span>
          ) : null}
          <span className="course-produce-date-range__course-meta">
            {routeTypeLabel}
            {summaryLabel ? ` · ${summaryLabel}` : null}
          </span>
        </div>
        {detailText ? (
          <p className="course-produce-date-range__course-detail">
            {detailText}
          </p>
        ) : null}
      </div>
      <p className="course-produce-date-range__hint">
        {t("course.PRODUCE_RIDES_DATE_RANGE_HINT")}
      </p>
      <LocalizationProvider>
        <div className="course-produce-date-range__pickers" dir={direction}>
          <DatePicker
            label={t("general.FROM_DATE")}
            format={dateFormat}
            value={dayjs.utc(dateFrom)}
            timezone={timezone}
            views={["year", "month", "day"]}
            onChange={handleFromChange}
            slotProps={{
              popper: {
                dir: direction,
                popperOptions: { modifiers: [] },
              },
            }}
          />
          <DatePicker
            label={t("general.UNTIL_DATE")}
            format={dateFormat}
            value={dayjs.utc(dateTo)}
            timezone={timezone}
            views={["year", "month", "day"]}
            onChange={handleToChange}
            slotProps={{
              popper: {
                dir: direction,
                popperOptions: { modifiers: [] },
              },
            }}
          />
        </div>
      </LocalizationProvider>
    </CourseProduceDateRangeDialogContentStyled>
  );
};

const CourseProduceDateRangeDialogContentStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "course-produce-date-range-dialog",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: min(360px, 100%);
  padding-top: 4px;

  .course-produce-date-range__course-block {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .course-produce-date-range__course {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${blueGreyMui._900};
    line-height: 1.35;
  }

  .course-produce-date-range__course-icon {
    display: inline-flex;
    align-items: center;
    line-height: 0;
  }

  .course-produce-date-range__course-id {
    font-weight: 600;
  }

  .course-produce-date-range__course-name {
    font-weight: 500;
  }

  .course-produce-date-range__course-meta {
    font-size: 12px;
    font-weight: 500;
    color: ${blueGreyMui._700};
  }

  .course-produce-date-range__course-detail {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
    font-weight: 400;
    color: ${blueGreyMui._600};
  }

  .course-produce-date-range__hint {
    margin: 6px 0 0;
    font-size: 13px;
    line-height: 1.45;
    color: ${blueGreyMui._700};
  }

  .course-produce-date-range__pickers {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .MuiFormControl-root {
      width: 100%;
    }
  }
`;
